import { createFileRoute } from '@tanstack/react-router';
import { requireAuth } from '../features/auth/guards.js';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/account/PageHeader.js';
import { Card } from '@/components/ui/card.js';
import { useUiStore, type Theme } from '@/stores/ui.js';
import { usePrefsStore } from '@/stores/prefs.js';
import { SUPPORTED_LOCALES } from '@/lib/i18n/index.js';

export const Route = createFileRoute('/_player/settings')({
  beforeLoad: requireAuth,
  component: SettingsScreen,
});

// ---- Sub-components ----

interface SettingsRowProps {
  title: string;
  subtitle?: string;
  action: React.ReactNode;
}

function SettingsRow({ title, subtitle, action }: SettingsRowProps) {
  return (
    <div className="flex items-center justify-between gap-4 py-4 px-5 border-b border-[var(--border-subtle)] last:border-0">
      <div className="min-w-0">
        <div className="text-[14px] font-semibold text-[var(--text-primary)]">{title}</div>
        {subtitle && (
          <div className="text-[12px] text-[var(--text-secondary)] mt-0.5">{subtitle}</div>
        )}
      </div>
      {action}
    </div>
  );
}

interface RadioGroupProps<T extends string> {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}

function RadioGroup<T extends string>({ options, value, onChange }: RadioGroupProps<T>) {
  return (
    <div className="inline-flex p-1 bg-[var(--bg-elevated)] rounded-lg border border-[var(--border)] h-10">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={[
            'px-3 rounded-md text-[13px] font-semibold transition-colors flex items-center',
            o.value === value
              ? 'bg-[var(--primary)] text-[var(--primary-on)]'
              : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
          ].join(' ')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const LOCALE_LABELS: Record<string, string> = {
  en: 'English',
  th: 'ไทย',
  vi: 'Tiếng Việt',
  zh: '中文',
  km: 'ខ្មែរ',
  lo: 'ລາວ',
  my: 'မြန်မာ',
};

// ---- Main screen ----

function SettingsScreen() {
  const { t } = useTranslation('settings');
  const theme    = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const locale    = usePrefsStore((s) => s.locale);
  const setLocale = usePrefsStore((s) => s.setLocale);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
      />

      {/* Appearance */}
      <section className="flex flex-col gap-3">
        <h2 className="text-[15px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
          {t('appearance.title')}
        </h2>
        <Card>
          <SettingsRow
            title={t('appearance.theme.title')}
            subtitle={t('appearance.theme.subtitle')}
            action={
              <RadioGroup<Theme>
                value={theme}
                onChange={setTheme}
                options={[
                  { value: 'dark',   label: t('appearance.theme.dark')   },
                  { value: 'light',  label: t('appearance.theme.light')  },
                  { value: 'system', label: t('appearance.theme.system') },
                ]}
              />
            }
          />
        </Card>
      </section>

      {/* Language */}
      <section className="flex flex-col gap-3">
        <h2 className="text-[15px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
          {t('language.title')}
        </h2>
        <Card className="p-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {SUPPORTED_LOCALES.map((l) => (
              <button
                key={l}
                onClick={() => setLocale(l)}
                className={[
                  'h-11 px-3 rounded-lg border text-[13px] font-semibold transition-colors flex items-center justify-between',
                  l === locale
                    ? 'border-[var(--primary)] bg-[var(--primary)]/5 text-[var(--text-primary)]'
                    : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]',
                ].join(' ')}
              >
                <span>{LOCALE_LABELS[l] ?? l}</span>
                <span className="font-mono text-[10px] uppercase text-[var(--text-muted)]">{l}</span>
              </button>
            ))}
          </div>
          <div className="text-[11px] text-[var(--text-muted)] mt-3">
            {t('language.hint')}
          </div>
        </Card>
      </section>

    </div>
  );
}
